import React, { useState, useEffect } from 'react'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { 
  Users, 
  Play, 
  Eye, 
  ShoppingCart, 
  TrendingUp,
  Loader,
  Award,
  AlertTriangle
} from 'lucide-react'
import { analyticsAPI, formatters } from '../services/analytics'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

function Conversions() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState('7daysAgo')

  useEffect(() => {
    fetchConversionsData()
  }, [dateRange])

  const fetchConversionsData = async () => {
    try {
      setLoading(true)
      setError(null)
      const conversions = await analyticsAPI.getConversions(dateRange)
      setData(conversions)
    } catch (err) {
      setError(err.message)
      console.error('Erro ao buscar dados de conversões:', err)
    } finally {
      setLoading(false)
    }
  }

  const getFunnelChartData = () => {
    if (!data?.funnelMetrics) return null

    const steps = [
      { label: 'Page View VSL', value: data.funnelMetrics.step1_pageview, color: '#3B82F6' },
      { label: 'Play VSL', value: data.funnelMetrics.step2_play, color: '#10B981' },
      { label: 'Viu Pith', value: data.funnelMetrics.step3_pith, color: '#F59E0B' },
      { label: 'Checkout', value: data.funnelMetrics.step4_checkout, color: '#EF4444' },
      { label: 'Purchase', value: data.funnelMetrics.step5_purchase, color: '#8B5CF6' }
    ]

    return {
      labels: steps.map(step => step.label),
      datasets: [{
        label: 'Usuários no Funil',
        data: steps.map(step => step.value),
        backgroundColor: steps.map(step => step.color),
        borderColor: steps.map(step => step.color),
        borderWidth: 2
      }]
    }
  }

  const getPerformanceColor = (performance) => {
    const colors = {
      'high': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800', 
      'low': 'bg-orange-100 text-orange-800',
      'very-low': 'bg-red-100 text-red-800'
    }
    return colors[performance] || 'bg-gray-100 text-gray-800'
  }

  const getFunnelHealthColor = (health) => {
    const colors = {
      'excellent': 'text-green-600',
      'good': 'text-blue-600',
      'average': 'text-yellow-600', 
      'poor': 'text-red-600'
    }
    return colors[health] || 'text-gray-600'
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${formatters.number(context.parsed.y)} usuários`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatters.number(value)
          }
        }
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-lg text-gray-600">Carregando dados...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchConversionsData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const funnelData = getFunnelChartData()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Análise de Conversões</h1>
          <p className="text-gray-600">Métricas de conversão e análise de performance</p>
        </div>
        
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 bg-white"
        >
          <option value="today">Hoje</option>
          <option value="yesterday">Ontem</option>
          <option value="7daysAgo">Últimos 7 dias</option>
          <option value="30daysAgo">Últimos 30 dias</option>
        </select>
      </div>

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Usuários Únicos */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Únicos</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatters.number(data?.summary?.totalUsers || 0)}
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        {/* Page View VSL */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Page View VSL</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatters.number(data?.summary?.pageviewVsl || 0)}
              </p>
            </div>
            <Eye className="w-8 h-8 text-green-600" />
          </div>
        </div>

        {/* Play VSL */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Play VSL</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatters.number(data?.summary?.playVsl || 0)}
              </p>
            </div>
            <Play className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        {/* Viu Pith */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Viu Pith</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatters.number(data?.summary?.viuPith || 0)}
              </p>
            </div>
            <Award className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        {/* % Pith/Play */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">% Pith/Play</p>
              <p className="text-2xl font-bold text-gray-900">
                {data?.summary?.pithPlayRate?.toFixed(2) || '0.00'}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        {/* Checkout */}
        <div className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Checkout</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatters.number(data?.summary?.beginCheckout || 0)}
              </p>
            </div>
            <ShoppingCart className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Funil de Conversão */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico do Funil */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Funil de Conversão</h3>
          <div className="h-64">
            {funnelData && <Bar data={funnelData} options={chartOptions} />}
          </div>
        </div>

        {/* Saúde do Funil */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Saúde do Funil</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status Geral:</span>
              <span className={`font-medium ${getFunnelHealthColor(data?.summary?.funnelHealth)}`}>
                {data?.summary?.funnelHealth === 'excellent' && 'Excelente'}
                {data?.summary?.funnelHealth === 'good' && 'Bom'}
                {data?.summary?.funnelHealth === 'average' && 'Médio'}
                {data?.summary?.funnelHealth === 'poor' && 'Ruim'}
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>PageView → Play:</span>
                <span>{data?.funnelMetrics?.dropoff?.pageviewToPlay || '0'}% perderam</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Play → Pith:</span>
                <span>{data?.funnelMetrics?.dropoff?.playToPith || '0'}% perderam</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Pith → Checkout:</span>
                <span>{data?.funnelMetrics?.dropoff?.pithToCheckout || '0'}% perderam</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Checkout → Purchase:</span>
                <span>{data?.funnelMetrics?.dropoff?.checkoutToPurchase || '0'}% perderam</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Campanhas */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance por Campanha</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campanha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PageView VSL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Play VSL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viu Pith
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Pith/Play
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Checkout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.campaignBreakdown?.slice(0, 10).map((campaign, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.campaign === '(not set)' ? 'Sem campanha' : campaign.campaign}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatters.number(campaign.pageview_vsl)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatters.number(campaign.play_vsl)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatters.number(campaign.viu_pith)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.pithPlayRate.toFixed(2)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatters.number(campaign.begin_checkout)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatters.number(campaign.purchase)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPerformanceColor(campaign.performance)}`}>
                      {campaign.performance === 'high' && 'Alto'}
                      {campaign.performance === 'medium' && 'Médio'}
                      {campaign.performance === 'low' && 'Baixo'}
                      {campaign.performance === 'very-low' && 'Muito Baixo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Conversions 